import api from '../store/api';
import { v4 as uuid } from 'uuid';
import { encode, decode, equal } from './appRecordCodec';

export const UTILITY_APPS = {
  growth_planner: 'growthPlannerData', fitness: 'fitnessData',
  libro_compras: 'libroComprasData', libro_ventas: 'libroVentasData',
  libro_gastos: 'libroGastosData', fastfood_pos_v1: 'fastfood_pos_v1',
};
const sessions = new Map();
const initializing = new Map();
const owner = () => {
  try { return localStorage.getItem('token') && JSON.parse(localStorage.getItem('user'))?._id; }
  catch { return null; }
};
const storageId = (user, app) => `app-records:${user}:${app}`;
const sessionId = (user, app) => `${user}:${app}`;
const notify = (app, error) => window.dispatchEvent(new CustomEvent('app-transaction-status', { detail: { app, error } }));
const persist = session => localStorage.setItem(storageId(session.user, session.app), JSON.stringify({ baseline: session.baseline, draft: session.draft, pending: session.pending }));
const assertOwner = session => {
  if (owner() !== session.user) throw new Error('La sesión cambió. Vuelve a abrir la app.');
};
const asMap = records => Object.fromEntries(records.map(record => [record.key, record]));
const values = baseline => Object.fromEntries(Object.entries(baseline).filter(([, row]) => row.value.type !== 'deleted').map(([key, row]) => [key, row.value]));
const endpoint = app => `/config/utility-apps/${app}`;
function changesFor(session) {
  const before = values(session.baseline), after = session.draft;
  return [...new Set([...Object.keys(before), ...Object.keys(after)])]
    .filter(key => !equal(before[key], after[key]))
    .map(key => ({ key, expectedVersion: session.baseline[key]?.version || 0,
      ...(key in after ? { value: after[key] } : { remove: true }) }));
}
async function drain(session) {
  assertOwner(session);
  while (session.pending || changesFor(session).length) {
    if (!session.pending) {
      session.pending = { requestId: uuid(), changes: changesFor(session) };
      persist(session);
    }
    const command = session.pending;
    assertOwner(session);
    const { data } = await api.post(`${endpoint(session.app)}/transactions`, command);
    assertOwner(session);
    const applied = Object.fromEntries(data.applied.map(row => [row.key, row.version]));
    command.changes.forEach(change => {
      if (change.remove) session.baseline[change.key] = { key: change.key, value: { type: 'deleted' }, version: applied[change.key] };
      else session.baseline[change.key] = { key: change.key, value: change.value, version: applied[change.key] };
    });
    session.pending = null;
    persist(session);
  }
  notify(session.app, null);
}
function flushSession(session) {
  if (session.sending) return session.sending;
  let completed = false;
  session.sending = Promise.resolve().then(() => drain(session)).then(() => { completed = true; }).catch(error => {
    notify(session.app, error.response?.data?.message || error.message);
    throw error;
  }).finally(() => {
    session.sending = null;
    // An editor effect can enqueue another change between drain finishing and
    // this promise settling (including after an initial no-op hydration).
    if (completed && (session.pending || changesFor(session).length)) return flushSession(session);
  });
  return session.sending;
}

// localStorage is a recoverable UI cache. Only per-record commands go to MongoDB.
export function saveUtilityCache(key, value) {
  const app = Object.keys(UTILITY_APPS).find(name => UTILITY_APPS[name] === key);
  const user = owner();
  const session = sessions.get(sessionId(user, app));
  const clean = JSON.parse(JSON.stringify(value));
  localStorage.setItem(key, JSON.stringify(clean));
  if (!user || !session) return;
  session.draft = encode(clean);
  persist(session);
  // Stores stay synchronous. Failures are retained durably and shown by the
  // boundary, with an explicit retry; a network failure never discards edits.
  flushSession(session).catch(() => {});
}

export async function openUtilityApp(app) {
  const user = owner();
  if (!user) return;
  const id = sessionId(user, app);
  if (initializing.has(id)) return initializing.get(id);
  const promise = (async () => {
    const key = UTILITY_APPS[app];
    let session = sessions.get(id);
    if (!session) {
      const saved = JSON.parse(localStorage.getItem(storageId(user, app)) || 'null');
      if (saved) {
        session = { user, app, ...saved };
        sessions.set(id, session);
        await flushSession(session);
      }
    } else await flushSession(session);
    const { data } = await api.get(endpoint(app));
    if (owner() !== user) throw new Error('La sesión cambió. Vuelve a abrir la app.');
    const baseline = asMap(data.records);
    session = { user, app, baseline, draft: values(baseline), pending: null };
    sessions.set(id, session);
    // Import old browser-only records once, never replace an existing server
    // record or infer deletions from an old browser snapshot.
    const marker = `${storageId(user, app)}:imported`;
    const cacheOwner = localStorage.getItem(`app-cache-owner:${key}`);
    const legacy = localStorage.getItem(key);
    if (!localStorage.getItem(marker) && legacy && (!cacheOwner || cacheOwner === user)) {
      localStorage.setItem(`${storageId(user, app)}:legacy-backup`, legacy);
      const local = encode(JSON.parse(legacy));
      Object.entries(baseline).forEach(([recordKey, row]) => {
        if (row.value.type === 'deleted') delete local[recordKey];
      });
      const merged = { ...local, ...session.draft };
      // Missing object/list containers are harmless; existing server values win.
      session.draft = merged;
      persist(session);
      await flushSession(session);
    }
    localStorage.setItem(marker, '1');
    localStorage.setItem(`app-cache-owner:${key}`, user);
    localStorage.setItem(key, JSON.stringify(decode(session.draft)));
    persist(session);
  })().catch(error => {
    const cached = sessions.get(id);
    if (!error.response && cached && owner() === user) {
      // A previously initialized account can keep working offline against its
      // own versions. Reconnect/retry submits the retained command IDs.
      const key = UTILITY_APPS[app];
      localStorage.setItem(key, JSON.stringify(decode(cached.draft)));
      localStorage.setItem(`app-cache-owner:${key}`, user);
      notify(app, error.message);
      return;
    }
    throw error;
  });
  initializing.set(id, promise);
  try { return await promise; } finally { initializing.delete(id); }
}

export async function flushUtilityApps() {
  const user = owner();
  if (!user) return;
  for (const app of Object.keys(UTILITY_APPS)) {
    const id = sessionId(user, app);
    if (!sessions.has(id)) {
      const saved = JSON.parse(localStorage.getItem(storageId(user, app)) || 'null');
      if (saved) sessions.set(id, { user, app, ...saved });
    }
  }
  for (const session of sessions.values()) {
    if (session.user === user) await flushSession(session);
  }
}

export async function retryUtilityApp(app) {
  const session = sessions.get(sessionId(owner(), app));
  if (session) await flushSession(session);
}

export async function reloadUtilityApp(app) {
  const user = owner();
  const id = sessionId(user, app);
  const session = sessions.get(id);
  if (session?.sending) { try { await session.sending; } catch { /* Kept below. */ } }
  const saved = localStorage.getItem(storageId(user, app));
  if (saved) localStorage.setItem(`${storageId(user, app)}:conflict-backup:${Date.now()}`, saved);
  sessions.delete(id);
  localStorage.removeItem(storageId(user, app));
  // This is an explicit server reload; the previous cache is a recovery backup.
  localStorage.setItem(`${storageId(user, app)}:imported`, '1');
  await openUtilityApp(app);
}
